import React, { useState, useEffect } from 'react';
import BASE from './api';
import { getToken, setToken } from './auth';
import Menu from './components/Menu';
import Cart from './components/Cart';
import OrderSuccess from './components/OrderSuccess';
import DrinkSelectPage from './components/DrinkSelectPage';
import ComboSelectPage from './components/ComboSelectPage';
import MealDrinkPage from './components/MealDrinkPage';
import DrinkFlavorPage from './components/DrinkFlavorPage';
import KitchenPage from './components/KitchenPage';
import ServingPage from './components/ServingPage';
import LoginPage from './components/LoginPage';
import './App.css';

function getRoute() {
  const p = window.location.pathname;
  if (p === '/kitchen/a') return 'kitchen-a';
  if (p === '/kitchen/b') return 'kitchen-b';
  if (p === '/serving') return 'serving';
  return 'customer';
}

function getTableFromUrl() {
  return new URLSearchParams(window.location.search).get('table') || '';
}

export default function App() {
  const route = getRoute();
  const params = new URLSearchParams(window.location.search);

  // OAuth callback：把 token 存起來並跳轉
  const authToken = params.get('auth_token');
  if (authToken) {
    setToken(authToken);
    const redirect = params.get('redirect') || '/serving';
    window.location.replace(redirect);
    return null;
  }

  const authError = params.get('auth_error');
  const authEmail = params.get('auth_email');

  // 廚房 / 櫃檯需要登入
  if (route === 'kitchen-a' || route === 'kitchen-b' || route === 'serving') {
    if (!getToken()) {
      const redirect = window.location.pathname;
      document.title = '登入';
      return <LoginPage redirect={redirect} error={authError} errorEmail={authEmail} />;
    }
    if (route === 'kitchen-a') { document.title = '小廚房'; return <KitchenPage kitchen="A" />; }
    if (route === 'kitchen-b') { document.title = '大廚房'; return <KitchenPage kitchen="B" />; }
    document.title = '櫃檯端'; return <ServingPage />;
  }

  document.title = '松鼠找食';
  return <CustomerApp defaultTable={getTableFromUrl()} />;
}

function CustomerApp({ defaultTable }) {
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [order, setOrder] = useState(null);
  const [drinkSelectItem, setDrinkSelectItem] = useState(null);
  const [comboSelectItem, setComboSelectItem] = useState(null);
  const [flavorSelectItem, setFlavorSelectItem] = useState(null);
  const [mealDrinkCallback, setMealDrinkCallback] = useState(null);
  const [orderMessage, setOrderMessage] = useState('');
  const [showOrderSuccess, setShowOrderSuccess] = useState(false);

  // 初始化時查詢該桌是否有未付款訂單
  useEffect(() => {
    if (!defaultTable.trim()) return;
    fetch(`${BASE}/api/orders/pending?table_number=${encodeURIComponent(defaultTable.trim())}`)
      .then(res => {
        if (res.status === 404) return null;
        if (!res.ok) throw new Error('查詢失敗');
        return res.json();
      })
      .then(data => { if (data) { setOrder(data); setShowOrderSuccess(true); } })
      .catch(err => console.error('查詢未付款訂單失敗:', err));
  }, [defaultTable]);

  const addToCart = (cartItem) => {
    setCart(prev => {
      const existing = prev.find(c => c.cartKey === cartItem.cartKey);
      if (existing) {
        return prev.map(c =>
          c.cartKey === cartItem.cartKey ? { ...c, quantity: c.quantity + 1 } : c
        );
      }
      return [...prev, { ...cartItem, quantity: 1 }];
    });
  };

  const updateQuantity = (cartKey, delta) => {
    setCart(prev =>
      prev
        .map(c => c.cartKey === cartKey ? { ...c, quantity: c.quantity + delta } : c)
        .filter(c => c.quantity > 0)
    );
  };

  const cartCount = cart.reduce((sum, c) => sum + c.quantity, 0);

  const handleOrderSubmit = async (items) => {
    try {
      const payload = {
        table_number: defaultTable.trim(),
        items: items.map(c => ({ menu_item_id: c.id, quantity: c.quantity, size: c.size })),
      };

      let method = order ? 'PATCH' : 'POST';
      let url = order ? `${BASE}/api/orders/${order.id}` : `${BASE}/api/orders`;
      let res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      // 若舊訂單已不存在或已付款，重新以 POST 建立新訂單
      if ((res.status === 404 || res.status === 400) && order) {
        setOrder(null);
        method = 'POST';
        url = `${BASE}/api/orders`;
        res = await fetch(url, {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }

      if (!res.ok) {
        const data = await res.json();
        const message = data.error || '訂單提交失敗';
        throw new Error(message);
      }

      const updatedOrder = await res.json();
      setOrder(updatedOrder);
      setCart([]);
      setOrderMessage('訂單已送出，廚房已收到。');
      setShowOrderSuccess(true);
      setTimeout(() => setOrderMessage(''), 4000);
    } catch (err) {
      console.error(err);
      setOrderMessage(err.message || '訂單送出失敗');
      setTimeout(() => setOrderMessage(''), 5000);
      throw err;
    }
  };

  const handleAddMore = () => {
    if (order && order.status === 'paid') {
      setOrder(null);
    }
    setShowOrderSuccess(false);
    setCart([]);
  };

  return (
    <div className="app">
      <header className="header">
        <div className="header-inner">
          <div className="logo-area">
            <span className="logo-icon">🐿️</span>
            <div>
              <h1 className="logo-title">松鼠找食</h1>
              <p className="logo-sub">現點現做・用心料理</p>
            </div>
          </div>
          <button className="cart-btn" onClick={() => setCartOpen(true)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
            </svg>
            <span>購物車</span>
            {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
          </button>
        </div>
      </header>

      {orderMessage && (
        <div className="order-status-message">{orderMessage}</div>
      )}

      <main className="main">
        {flavorSelectItem ? (
          <DrinkFlavorPage
            item={flavorSelectItem}
            cart={cart}
            onAddToCart={addToCart}
            onUpdateQuantity={updateQuantity}
            onBack={() => setFlavorSelectItem(null)}
          />
        ) : comboSelectItem ? (
          <>
            <ComboSelectPage
              item={comboSelectItem}
              cart={cart}
              onAddToCart={addToCart}
              onUpdateQuantity={updateQuantity}
              onBack={() => setComboSelectItem(null)}
              onOpenMealDrinkPage={cb => setMealDrinkCallback(() => cb)}
            />
            {mealDrinkCallback && (
              <MealDrinkPage
                onConfirm={drinkInfo => {
                  mealDrinkCallback(drinkInfo);
                  setMealDrinkCallback(null);
                }}
                onBack={() => setMealDrinkCallback(null)}
              />
            )}
          </>
        ) : drinkSelectItem ? (
          <DrinkSelectPage
            item={drinkSelectItem}
            cart={cart}
            onAddToCart={addToCart}
            onUpdateQuantity={updateQuantity}
            onBack={() => setDrinkSelectItem(null)}
          />
        ) : showOrderSuccess && order ? (
          <OrderSuccess order={order} onAddMore={handleAddMore} />
        ) : (
          <Menu onAddToCart={addToCart} onUpdateQuantity={updateQuantity} cart={cart} onOpenDrinkSelect={setDrinkSelectItem} onOpenComboSelect={setComboSelectItem} onOpenFlavorSelect={setFlavorSelectItem} />
        )}
      </main>

      {cartOpen && (
        <Cart
          cart={cart}
          defaultTable={defaultTable}
          onUpdateQuantity={updateQuantity}
          onClose={() => setCartOpen(false)}
          order={order}
          onOrderSubmit={handleOrderSubmit}
        />
      )}
    </div>
  );
}
